import { useEffect, useState, useMemo, useRef } from "react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { Send, Loader2 as Loader2Icon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Chat,
  DownloadSimple,
  FileText,
  ThumbsDown,
  ThumbsUp,
  Funnel,
  ArchiveBox,
  ArrowUUpLeft,
  ListChecks,
  ArchiveTray,
  CaretLeft,
  CaretRight,
  ArrowLeft,
  X,
  GearSix,
  CalendarBlank,
  Flag,
  CircleDashed,
  Tag,
  DotsThreeVertical,
} from "@phosphor-icons/react";

// Import DropdownMenu components for status filter
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

// Import VC API functions
import {
  getStartupPitchToVCByVC,
  markPitchInterested,
  markPitchUninterested,
  markPitchRead,
  getConversationUuidForPitch,
  archivePitch,
  unarchivePitch,
  getInvestorPitchPreferences,
} from "@/services/api/VentureCapital";
import ScoreBadge, {
  ScoreComponentIndicator,
} from "@/components/ui/ScoreBadge";
import InvestorPreferencesModal from "@/components/modal/InvestorPreferencesModal";

// Import Conversation API functions
import {
  getConversationMessages,
  sendPitchMessage,
  listUserConversations,
} from "@/services/api/Conversations";

// Import useAuth to get current user ID
import useAuth from "@/hooks/auth";
// Import hooks for country and sector data
import { useCountry } from "@/hooks/useCountry";
import { useSector } from "@/hooks/useSector";
import { Input } from "@/components/ui/input";

import NotFound from "@/assets/images/not-found.png";
import DummyLogo from "@/assets/images/lucidity-placeholder.png";
import getEcho from "@/lib/echo"; // Import getEcho
import ChatWindow from "@/components/chat/ChatWindow"; // Import ChatWindow
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// TODO: Define API structure for messages (e.g., assuming { id, sender_id, recipient_id, text, created_at, sender_type: 'user'|'startup' })

// Helper function to get startup information from either startup object or startup_data
const getStartupInfo = (pitch) => {
  if (!pitch) {
    return null;
  }

  if (pitch.startup) {
    return pitch.startup;
  }

  if (pitch.startup_data) {
    if (typeof pitch.startup_data === "object") {
      return pitch.startup_data;
    }

    try {
      return JSON.parse(pitch.startup_data);
    } catch (error) {
      console.error("Failed to parse startup_data:", error);
      return null;
    }
  }

  return null;
};

const getPitchRevenueLabel = (revenueStatus) => {
  const labels = {
    pre: "Pre-revenue",
    post: "Post-revenue",
    pre_revenue: "Pre-revenue",
    post_revenue: "Post-revenue",
  };

  return labels[revenueStatus] || revenueStatus;
};

const getPitchMetadata = (pitch, startupInfo) => {
  const sectors =
    pitch.pitch_sectors?.map((sector) => sector.name).filter(Boolean) ||
    (pitch.sector ? [pitch.sector] : []);
  const stage =
    typeof pitch.stage === "string" ? pitch.stage : pitch.stage?.name;
  const revenue = getPitchRevenueLabel(pitch.revenue_status || pitch.revenue);
  const markets = [
    ...(pitch.is_global_market ? ["Global"] : []),
    ...(pitch.market_regions?.map((region) => region.name).filter(Boolean) ||
      []),
    ...(pitch.market_countries
      ?.map((country) => country.country_name || country.name)
      .filter(Boolean) || []),
  ];
  const fallbackMarket =
    pitch.markets ||
    (startupInfo?.city?.name && startupInfo?.country?.country_name
      ? `${startupInfo.city.name}, ${startupInfo.country.country_name}`
      : startupInfo?.country?.country_name);

  return [
    ...sectors,
    stage,
    revenue,
    ...(markets.length > 0 ? markets : [fallbackMarket]),
  ].filter(Boolean);
};

const getScoreComponentRatio = (component) => {
  if (component === null || component === undefined) return null;

  if (typeof component === "number") {
    return component > 1 ? component / 100 : component;
  }

  if (typeof component !== "object") return null;
  if (component.status === "agnostic") return null;

  const rawPct = Number(component.raw_pct);
  if (Number.isFinite(rawPct)) return rawPct / 100;

  const earned = Number(component.earned);
  const weight = Number(component.weight);
  if (Number.isFinite(earned) && Number.isFinite(weight) && weight > 0) {
    return earned / weight;
  }

  return null;
};

export default function PitchBankList({ vcId, vcName, vcSlug, isAngel, initialOpenPrefs }) {
  const router = useRouter();
  const { user } = useAuth(); // Get current user info
  // State previously in parent components
  const [pitchHistories, setPitchHistories] = useState([]);
  const [loadingPitches, setLoadingPitches] = useState(true);
  const [sortBy, setSortBy] = useState("score");
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [currentPitchForFeedback, setCurrentPitchForFeedback] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  // --- Filter State ---
  const [filterStatus, setFilterStatus] = useState([]); // No longer includes 'archived'
  const [openStatusFilter, setOpenStatusFilter] = useState(false);
  const [tempFilterStatus, setTempFilterStatus] = useState([]);
  // Country and Sector filter states
  const [filterCountries, setFilterCountries] = useState([]);
  const [filterSectors, setFilterSectors] = useState([]);
  const [openCountryFilter, setOpenCountryFilter] = useState(false);
  const [openSectorFilter, setOpenSectorFilter] = useState(false);
  const [searchCountryQuery, setSearchCountryQuery] = useState("");
  const [searchSectorQuery, setSearchSectorQuery] = useState("");
  // Temporary states for applying filters
  const [tempFilterCountries, setTempFilterCountries] = useState([]);
  const [tempFilterSectors, setTempFilterSectors] = useState([]);
  // Hook calls for data
  const { countries } = useCountry({ searchStartupDropdown: true });
  const { data: sectors } = useSector({ searchStartupDropdown: true });
  // --------------------
  const [chatSheetOpen, setChatSheetOpen] = useState(false);
  const [currentChatPitch, setCurrentChatPitch] = useState(null);
  const [currentMessages, setCurrentMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageInputValue, setMessageInputValue] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [currentView, setCurrentView] = useState("active"); // 'active' or 'archived'

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState(null); // Stores the whole pagination object from API
  const [perPage, setPerPage] = useState(15); // Default per page

  // Smart Pitch Bank state
  const [smartFilteringMode, setSmartFilteringMode] = useState(null); // null | 'ranking_only' | 'smart_pitch_bank'
  const [activeTier, setActiveTier] = useState("recommended"); // 'recommended' | 'other'
  const [recommendedCount, setRecommendedCount] = useState(0);
  const [otherCount, setOtherCount] = useState(0);
  const [recommendedThreshold, setRecommendedThreshold] = useState(65);
  const [prefsModalOpen, setPrefsModalOpen] = useState(!!initialOpenPrefs);
  const [mobileFilterSheetOpen, setMobileFilterSheetOpen] = useState(false);
  const latestFetchStateRef = useRef({});

  useEffect(() => {
    latestFetchStateRef.current = {
      sortBy,
      filterStatus,
      currentView,
      filterCountries,
      filterSectors,
      activeTier,
      smartFilteringMode,
    };
  }, [
    sortBy,
    filterStatus,
    currentView,
    filterCountries,
    filterSectors,
    activeTier,
    smartFilteringMode,
  ]);

  const clearOpenPreferencesQuery = () => {
    if (!router.query.open_preferences) return;

    const { open_preferences, preference_vc_id, ...query } = router.query;
    router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
    });
  };

  const handlePrefsModalOpenChange = (nextOpen) => {
    setPrefsModalOpen(nextOpen);
    if (!nextOpen) clearOpenPreferencesQuery();
  };

  // Details panel state
  const [detailsPitch, setDetailsPitch] = useState(null);

  // New Chat State
  const [chatViewMode, setChatViewMode] = useState("list"); // 'list' or 'detail'
  const [selectedChatConversation, setSelectedChatConversation] =
    useState(null); // Holds the full conversation object
  const [vcConversations, setVcConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(false);
  const [isSubmittingUninterested, setIsSubmittingUninterested] =
    useState(false);
  // New state for the send message confirmation dialog
  const [showSendMessageConfirmDialog, setShowSendMessageConfirmDialog] =
    useState(false);
  const [pitchForSendMessageConfirm, setPitchForSendMessageConfirm] =
    useState(null);

  // Fetch investor preferences to detect smart_filtering_mode
  const fetchPreferences = async (id) => {
    try {
      const res = await getInvestorPitchPreferences(id);
      const mode = res.data?.data?.smart_filtering_mode || "ranking_only";
      setSmartFilteringMode(mode);
      return mode;
    } catch {
      setSmartFilteringMode("ranking_only");
      return "ranking_only";
    }
  };

  const fetchSmartPitchCounts = async (baseParams = {}) => {
    if (!vcId) return;

    const { filter_tier, page, per_page, sort_by, ...countParams } = baseParams;

    try {
      const res = await getStartupPitchToVCByVC(vcId, {
        params: {
          ...countParams,
          page: 1,
          per_page: 1,
        },
      });
      const meta = res.data?.meta;
      if (!meta) return;
      if (meta.recommended_count !== undefined)
        setRecommendedCount(meta.recommended_count);
      if (meta.other_count !== undefined) setOtherCount(meta.other_count);
      if (meta.threshold !== undefined)
        setRecommendedThreshold(meta.threshold);
    } catch (error) {
      console.error("Failed to fetch smart pitch counts:", error);
    }
  };

  // Function to fetch pitches with current sort and filters
  const fetchPitches = async (
    pageToFetch = 1,
    currentSortBy = sortBy,
    currentFilterStatus = filterStatus,
    view = currentView,
    currentFilterCountries = filterCountries,
    currentFilterSectors = filterSectors,
    currentTier = activeTier,
    currentMode = smartFilteringMode,
  ) => {
    if (!vcId) {
      setLoadingPitches(false);
      setPitchHistories([]);
      setPaginationData(null);
      return;
    }
    setLoadingPitches(true);
    try {
      const sortParamMap = {
        date_desc: "newest",
        date_asc: "oldest",
        score: "score",
        status: "status",
        unread: "unread",
      };
      let sortParam = sortParamMap[currentSortBy] || currentSortBy;
      if (
        currentMode === "smart_pitch_bank" &&
        currentTier === "other" &&
        currentSortBy === "score"
      ) {
        sortParam = "newest";
      }
      const params = {
        sort_by: sortParam,
        is_archived: view === "archived",
        page: pageToFetch,
        per_page: perPage,
      };

      const actualStatusFilters = currentFilterStatus.filter(
        (s) => s === "interested" || s === "uninterested",
      );
      if (actualStatusFilters.length > 0) {
        params.filter_status = actualStatusFilters.join(",");
      }
      if (currentFilterStatus.includes("unread")) {
        params.filter_read = "false";
      }

      // Add country filter
      if (currentFilterCountries.length > 0) {
        params.filter_countries = currentFilterCountries
          .map((c) => c.id)
          .join(",");
      }

      // Add sector filter
      if (currentFilterSectors.length > 0) {
        params.filter_sectors = currentFilterSectors.map((s) => s.id).join(",");
      }

      // The default smart endpoint returns the Recommended tab plus tab counts.
      // Use filter_tier only for the Other tab; otherwise counts can go stale.
      if (
        currentMode === "smart_pitch_bank" &&
        currentTier !== "recommended"
      ) {
        params.filter_tier = currentTier;
      }

      const config = { params };
      const res = await getStartupPitchToVCByVC(vcId, config);

      // Handle different response structures
      // smart_pitch_bank: { data: { paginator }, meta: { ... } }
      // ranking_only: standard paginator
      if (res.data?.meta) {
        const paginator = res.data.data;
        setPitchHistories(paginator.data || []);
        setPaginationData(paginator);
        setCurrentPage(paginator.current_page || 1);
        const hasRecommendedCount =
          res.data.meta.recommended_count !== undefined;
        const hasOtherCount = res.data.meta.other_count !== undefined;
        if (hasRecommendedCount)
          setRecommendedCount(res.data.meta.recommended_count);
        if (hasOtherCount)
          setOtherCount(res.data.meta.other_count);
        if (res.data.meta.threshold !== undefined)
          setRecommendedThreshold(res.data.meta.threshold);
        if (
          currentMode === "smart_pitch_bank" &&
          (!hasRecommendedCount || !hasOtherCount)
        ) {
          if (currentTier === "recommended") {
            setRecommendedCount(paginator.total || 0);
          } else {
            setOtherCount(paginator.total || 0);
          }
          fetchSmartPitchCounts(params);
        }
      } else {
        setPitchHistories(res.data.data || []);
        setPaginationData(res.data);
        setCurrentPage(res.data.current_page || 1);
      }
    } catch (error) {
      console.error("Failed to fetch pitch histories:", error);
      toast.error("Failed to load pitches.");
      setPitchHistories([]);
      setPaginationData(null);
    } finally {
      setLoadingPitches(false);
    }
  };

  // Fetch conversations relevant to this VC/Angel context
  // TODO: Ideally, the API should allow filtering conversations by VC/Angel ID.
  // For now, fetching all user conversations and maybe filtering client-side if needed,
  // or just displaying all is the fallback.
  const fetchConversations = async () => {
    if (!user) return;
    setLoadingConversations(true);
    try {
      const response = await listUserConversations();
      // Assuming the API returns conversations with pitch_id or startup_id to link them
      // Or perhaps filter based on participants if VC info is available there
      const allConversations = response.data?.data || [];
      // Placeholder: Filter conversations somehow related to this VC
      // This needs refinement based on API capabilities.
      // For now, let's assume we display all related for simplicity.
      // We might need to match participant IDs or look for context.
      // If the API supports context (like vc_id), use that.
      // Example (conceptual - adjust based on actual data):
      const relevantConversations = allConversations.filter((conv) => {
        // Check if the conversation involves the VC (user) and a startup from pitches
        // Or if the API provides a direct link (e.g., conv.context.vc_id === vcId)
        // This is a placeholder logic.
        const otherParticipant = conv.display_participants?.[0];
        return pitchHistories.some(
          (p) => p.startup?.id === otherParticipant?.id,
        ); // Example: Match by startup ID if possible
        // OR return conv.context?.vc_id === vcId; // If API provides this
      });
      // If filtering is too complex/unreliable, start by showing all:
      setVcConversations(allConversations);
      // setVcConversations(relevantConversations); // Use this if filtering is implemented
    } catch (error) {
      console.error("Failed to fetch conversations for VC:", error);
      toast.error("Could not load conversations.");
      setVcConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Effect for initial load and when vcId changes (resets page to 1)
  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when vcId changes
    // Reset filters when vcId changes
    setFilterStatus([]);
    setTempFilterStatus([]);
    setFilterCountries([]);
    setFilterSectors([]);
    setTempFilterCountries([]);
    setTempFilterSectors([]);
    setActiveTier("recommended");
    setSmartFilteringMode(null);

    if (vcId) {
      fetchPreferences(vcId).then((mode) => {
        fetchPitches(1, sortBy, [], currentView, [], [], "recommended", mode);
      });
    }
  }, [vcId]);

  // Effect for when sortBy, filterStatus, currentView, or tier changes (resets page to 1)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    fetchPitches(
      1,
      sortBy,
      filterStatus,
      currentView,
      filterCountries,
      filterSectors,
      activeTier,
      smartFilteringMode,
    );
  }, [
    sortBy,
    filterStatus,
    currentView,
    filterCountries,
    filterSectors,
    activeTier,
  ]);

  // Effect for when currentPage changes (fetches the specific page)
  useEffect(() => {
    if (vcId) {
      fetchPitches(
        currentPage,
        sortBy,
        filterStatus,
        currentView,
        filterCountries,
        filterSectors,
        activeTier,
        smartFilteringMode,
      );
    }
  }, [currentPage]);

  // Effect to fetch conversations when vcId or user changes
  useEffect(() => {
    if (vcId && user) {
      fetchConversations();
      // When VC changes, reset chat view
      setChatViewMode("list");
      setSelectedChatConversation(null);
      setCurrentMessages([]);
    }
  }, [vcId, user]);

  // --- Handlers ---

  const handleMarkInterested = async (pitchId) => {
    const previousPitches = [...pitchHistories];
    try {
      await markPitchInterested(pitchId);
      toast.success("Pitch marked as interested.");
      const pitchNeedsUnarchiving = pitchHistories.find(
        (p) => p.id === pitchId && p.is_archived,
      );
      if (pitchNeedsUnarchiving) {
        await unarchivePitch(pitchId);
        toast.success("Pitch also unarchived.");
      }
      // Refetch pitches to get the latest state (including potential unarchive)
      await fetchPitches(currentPage, sortBy, filterStatus, currentView);

      // Find the updated pitch object from the newly fetched list
      // We need to access the state *after* fetchPitches completes.
      // Using a .then or awaiting might not work directly with setState updates.
      // A slight delay or useEffect might be needed, but let's try finding it directly first.
      // Re-fetch is async, so accessing pitchHistories immediately might not work.
      // Instead, let's find the original pitch data to show the dialog.
      const originalPitch =
        previousPitches.find((p) => p.id === pitchId) ||
        pitchHistories.find((p) => p.id === pitchId);

      if (originalPitch) {
        // Trigger the confirmation dialog
        setPitchForSendMessageConfirm(originalPitch);
        setShowSendMessageConfirmDialog(true);
      } else {
        // If pitch not found (shouldn't happen often), just log it.
        console.warn(
          "Could not find pitch data after marking interested to show send message prompt.",
        );
      }
    } catch (error) {
      console.error("Failed to mark pitch as interested:", error);
      toast.error("Failed to mark pitch as interested.");
    }
  };

  const handleOpenFeedbackDialog = (pitch) => {
    setCurrentPitchForFeedback(pitch);
    setFeedbackText("");
    setFeedbackDialogOpen(true);
  };

  const handleMarkUninterested = async (pitchId, feedback = "") => {
    if (!pitchId) return;
    setIsSubmittingUninterested(true);
    try {
      const payload = feedback ? { feedback } : {};
      await markPitchUninterested(pitchId, payload);
      toast.success(
        `Pitch marked as uninterested${feedback ? " with feedback" : ""}.`,
      );
      const pitch = pitchHistories.find(
        (p) => p.id === pitchId && p.is_archived,
      );
      if (pitch) {
        await unarchivePitch(pitchId);
        toast.success("Pitch also unarchived.");
      }
      setFeedbackDialogOpen(false);
      fetchPitches(currentPage, sortBy, filterStatus, currentView);
    } catch (error) {
      console.error("Failed to mark pitch as uninterested:", error);
      toast.error("Failed to mark pitch as uninterested.");
    } finally {
      setIsSubmittingUninterested(false);
    }
  };

  const handleMarkRead = async (pitchId) => {
    const pitch = pitchHistories.find((p) => p.id === pitchId);
    if (!pitch || pitch.is_read || pitch.is_archived) return;
    // Optimistic update for is_read locally
    setPitchHistories((prev) =>
      prev.map((p) => (p.id === pitchId ? { ...p, is_read: true } : p)),
    );
    try {
      await markPitchRead(pitchId);
      // No full refetch needed, just local update is fine for is_read visual state
    } catch (error) {
      console.error("Failed to mark pitch as read:", error);
      // Revert optimistic update if API call fails for marking as read
      setPitchHistories((prev) =>
        prev.map((p) => (p.id === pitchId ? { ...p, is_read: false } : p)),
      );
    }
  };

  const handleArchivePitch = async (pitchId) => {
    try {
      await archivePitch(pitchId);
      toast.success("Pitch archived.");
      // If on active view, the item might disappear. If total items decrease such that current page is invalid, go to last valid page.
      if (currentView === "active") {
        if (paginationData && pitchHistories.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1); // Go to previous page if it was the last item on current page > 1
        } else {
          fetchPitches(currentPage, sortBy, filterStatus, currentView); // Refetch current page
        }
      } else {
        // If on archived view, refetch to show the newly archived item
        fetchPitches(currentPage, sortBy, filterStatus, currentView);
      }
    } catch (error) {
      console.error("Failed to archive pitch:", error);
      toast.error("Failed to archive pitch.");
    }
  };

  const handleUnarchivePitch = async (pitchId) => {
    try {
      await unarchivePitch(pitchId);
      toast.success("Pitch unarchived and moved to active.");
      if (currentView === "archived") {
        if (paginationData && pitchHistories.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchPitches(currentPage, sortBy, filterStatus, currentView);
        }
      } else {
        // If on active view, refetch to show the newly unarchived item
        fetchPitches(currentPage, sortBy, filterStatus, currentView);
      }
    } catch (error) {
      console.error("Failed to unarchive pitch:", error);
      toast.error("Failed to unarchive pitch.");
    }
  };

  // Modified handleSendMessageClick to ensure panel is open
  const handleSendMessageClick = async (pitch) => {
    const startupInfo = getStartupInfo(pitch);
    setIsChatPanelOpen(true); // Ensure panel is open
    setCurrentChatPitch(pitch);
    setChatViewMode("detail");
    setLoadingMessages(true);
    setCurrentMessages([]);
    setSelectedChatConversation(null);

    try {
      const convResponse = await getConversationUuidForPitch(pitch.id);
      if (
        !convResponse ||
        !convResponse.data ||
        !convResponse.data.success ||
        !convResponse.data.conversation_uuid
      ) {
        throw new Error("Could not get conversation UUID.");
      }
      const conversationUuid = convResponse.data.conversation_uuid;
      let conversation = vcConversations.find(
        (c) => c.uuid === conversationUuid,
      );
      if (!conversation) {
        await fetchConversations();
        conversation = vcConversations.find((c) => c.uuid === conversationUuid);
        if (!conversation) {
          console.warn(
            "Conversation details not found after fetching UUID. UI might lack participant info.",
          );
          conversation = {
            uuid: conversationUuid,
            display_participants: [
              {
                id: startupInfo?.id,
                name: startupInfo?.name,
                logo_url: startupInfo?.logo_url,
              },
            ],
          };
        }
      }
      setSelectedChatConversation(conversation);
      const messagesResponse = await getConversationMessages(conversationUuid);
      const rawMessages = messagesResponse.data.data || [];
      const formattedMessages = rawMessages.map((msg) => {
        // More robust sender determination
        const isCurrentUser =
          msg.sender_info?.id === user?.id ||
          msg.sender_info?.is_me === true ||
          String(msg.sender_info?.id) === String(user?.id);

        return {
          sender: isCurrentUser ? "user" : "other",
          text: msg.content,
          timestamp: msg.created_at,
          id: msg.id,
          logo_url: msg.sender_info.logo_url || DummyLogo,
        };
      });
      setCurrentMessages(formattedMessages);
      if (!pitch.conversation_uuid) {
        setPitchHistories((prev) =>
          prev.map((p) =>
            p.id === pitch.id
              ? { ...p, conversation_uuid: conversationUuid }
              : p,
          ),
        );
      }
    } catch (error) {
      console.error("Error preparing chat:", error);
      setCurrentMessages([]);
      toast.error("Failed to load chat details. Please try again.");
      setChatViewMode("list");
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSelectConversation = async (conversation) => {
    if (!conversation || !conversation.uuid) return;
    setIsChatPanelOpen(true); // Ensure panel is open
    setSelectedChatConversation(conversation);
    setChatViewMode("detail");
    setLoadingMessages(true);
    setCurrentMessages([]);
    try {
      const messagesResponse = await getConversationMessages(conversation.uuid);
      const rawMessages = messagesResponse.data.data || [];
      const formattedMessages = rawMessages.map((msg) => {
        // More robust sender determination
        const isCurrentUser =
          msg.sender_info?.id === user?.id ||
          msg.sender_info?.is_me === true ||
          String(msg.sender_info?.id) === String(user?.id);

        return {
          sender: isCurrentUser ? "user" : "other",
          text: msg.content,
          timestamp: msg.created_at,
          id: msg.id,
          logo_url: msg.sender_info.logo_url || DummyLogo,
        };
      });
      setCurrentMessages(formattedMessages);
    } catch (error) {
      console.error(
        `Failed to fetch messages for conversation ${conversation.uuid}:`,
        error,
      );
      toast.error("Could not load messages for this conversation.");
      setCurrentMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Modified to accept text - state/input managed by ChatWindow
  const handleSendMessageSubmit = async (text) => {
    if (!selectedChatConversation || !selectedChatConversation.uuid || !text)
      return;
    setSendingMessage(true);
    console.log("[PitchBankList] handleSendMessageSubmit: Text to send:", text);

    // Optimistic update
    const optimisticMessage = {
      sender: "user",
      text: text,
      timestamp: new Date().toISOString(),
      optimisticId: Date.now(),
      logo_url: user?.profile_photo_url || DummyLogo,
    };
    console.log(
      "[PitchBankList] handleSendMessageSubmit: Optimistic message created:",
      optimisticMessage,
    );
    setCurrentMessages((prev) => {
      const newMessages = [...prev, optimisticMessage];
      console.log(
        "[PitchBankList] handleSendMessageSubmit: setCurrentMessages with optimistic update. New messages:",
        newMessages,
      );
      return newMessages;
    });

    try {
      const payload = { content: text };
      const response = await sendPitchMessage(
        selectedChatConversation.pitch_id || currentChatPitch?.id,
        payload,
      );
      console.log(
        "[PitchBankList] handleSendMessageSubmit: API response received:",
        response,
      );

      if (response.data && response.data.data) {
        const sentMessage = response.data.data;
        console.log(
          "[PitchBankList] handleSendMessageSubmit: Message from API (sentMessage):",
          sentMessage,
        );
        const formattedSentMessage = {
          sender: "user", // Always "user" since this is the message we just sent
          text: sentMessage.content,
          timestamp: sentMessage.created_at,
          id: sentMessage.id,
          logo_url:
            sentMessage.sender_info?.logo_url ||
            user?.profile_photo_url ||
            DummyLogo,
        };
        console.log(
          "[PitchBankList] handleSendMessageSubmit: Formatted message from API:",
          formattedSentMessage,
        );
        setCurrentMessages((prev) => {
          const updatedMessages = prev.map((msg) =>
            msg.optimisticId === optimisticMessage.optimisticId
              ? formattedSentMessage
              : msg,
          );
          console.log(
            "[PitchBankList] handleSendMessageSubmit: setCurrentMessages after API success. Updated messages:",
            updatedMessages,
          );
          return updatedMessages;
        });
      } else {
        console.warn(
          "[PitchBankList] handleSendMessageSubmit: Send message response did not contain expected data (response.data or response.data.data is missing).",
          response,
        );
        setCurrentMessages((prev) => {
          const revertedMessages = prev.map((msg) =>
            msg.optimisticId === optimisticMessage.optimisticId
              ? { ...msg, optimisticId: undefined, error: true }
              : msg,
          );
          console.log(
            "[PitchBankList] handleSendMessageSubmit: setCurrentMessages after API failure/bad data. Reverted/marked messages:",
            revertedMessages,
          );
          return revertedMessages;
        });
      }

      // If this was the first message, the conversation might be newly created.
      // Refetch conversation list to ensure it's included.
      // This assumes sendPitchMessage might create the conversation record backend.
      if (
        !vcConversations.some((c) => c.uuid === selectedChatConversation.uuid)
      ) {
        fetchConversations();
      }
    } catch (error) {
      console.error(
        "[PitchBankList] handleSendMessageSubmit: Error sending message:",
        error,
      );
      toast.error(
        `Failed to send message. ${error.response?.data?.message || ""}`,
      );
      setCurrentMessages((prev) => {
        const errorMessages = prev.filter(
          (msg) => msg.optimisticId !== optimisticMessage.optimisticId,
        );
        console.log(
          "[PitchBankList] handleSendMessageSubmit: setCurrentMessages after catch. Error messages:",
          errorMessages,
        );
        return errorMessages;
      });
      if (error.response?.status === 403) {
        toast.error(
          "Cannot send message. Pitch must be marked as 'Interested' first.",
        );
      }
    } finally {
      console.log(
        "[PitchBankList] handleSendMessageSubmit: Firing finally block.",
      );
      setSendingMessage(false);
    }
  };

  // Effect for real-time messages in the detail view
  useEffect(() => {
    // Only listen when in detail view and a conversation is selected
    if (
      chatViewMode !== "detail" ||
      !selectedChatConversation?.uuid ||
      !user?.id
    ) {
      return;
    }

    const echo = getEcho();
    if (!echo) {
      console.warn(
        "Echo not available in PitchBankList chat, real-time updates disabled.",
      );
      return;
    }

    const conversationUuid = selectedChatConversation.uuid;
    console.log(
      `Attempting to subscribe to conversation.${conversationUuid} from PitchBankList (Detail View)`,
    );
    const channel = echo.private(`conversation.${conversationUuid}`);

    const messageListener = (event) => {
      console.log(
        "Real-time message received via Echo in PitchBankList (Detail View):",
        event,
      );

      // More robust sender determination
      const isCurrentUser =
        event.sender_info?.id === user?.id ||
        event.sender_info?.is_me === true ||
        String(event.sender_info?.id) === String(user?.id);

      const newMessage = {
        sender: isCurrentUser ? "user" : "other",
        text: event.content,
        timestamp: event.created_at,
        id: event.id,
        logo_url: event.sender_info?.logo_url || DummyLogo,
      };

      setCurrentMessages((prevMessages) => {
        // Avoid duplicates based on message ID or optimistic ID
        if (prevMessages.some((msg) => msg.id === newMessage.id)) {
          console.log(
            "Duplicate message detected via ID, skipping:",
            newMessage.id,
          );
          return prevMessages;
        }

        // Also check if this message should replace an optimistic message
        // This handles the case where the real-time event arrives before the API response
        const hasOptimisticMessage = prevMessages.some(
          (msg) =>
            msg.optimisticId &&
            msg.text === newMessage.text &&
            msg.sender === newMessage.sender &&
            Math.abs(
              new Date(msg.timestamp).getTime() -
                new Date(newMessage.timestamp).getTime(),
            ) < 5000, // Within 5 seconds
        );

        if (hasOptimisticMessage) {
          console.log("Replacing optimistic message with real-time message");
          return prevMessages.map((msg) =>
            msg.optimisticId &&
            msg.text === newMessage.text &&
            msg.sender === newMessage.sender &&
            Math.abs(
              new Date(msg.timestamp).getTime() -
                new Date(newMessage.timestamp).getTime(),
            ) < 5000
              ? newMessage
              : msg,
          );
        }

        return [...prevMessages, newMessage];
      });
      // TODO: Potentially update conversation list (vcConversations) with latest message info if needed
    };

    channel.listen(".message.sent", messageListener);

    channel.error((error) => {
      console.error(
        `Error subscribing to Echo channel conversation.${conversationUuid} in PitchBankList (Detail View):`,
        error,
      );
    });
    channel.on("pusher:subscription_succeeded", () =>
      console.log(
        `Successfully subscribed to conversation.${conversationUuid} (Detail)`,
      ),
    );
    channel.on("pusher:subscription_error", (status) =>
      console.error(
        `Subscription failed for conversation.${conversationUuid} (Detail): status ${status}`,
      ),
    );

    // Cleanup when conversation changes, view changes, or component unmounts
    return () => {
      if (echo && conversationUuid) {
        console.log(
          `Leaving Echo channel: conversation.${conversationUuid} from PitchBankList (Detail View)`,
        );
        channel.stopListening(".message.sent", messageListener); // Stop specific listener
        echo.leave(`conversation.${conversationUuid}`);
      }
    };
  }, [chatViewMode, selectedChatConversation?.uuid, user?.id]); // Re-subscribe if view or selection changes

  // Effect for real-time updates to the conversation list (e.g., new message in *any* conversation)
  // This might require listening to a more general channel or periodic refetching if Echo doesn't provide a user-level channel easily.
  useEffect(() => {
    // Placeholder for potential list updates via Echo or polling
    // Example using Echo if a user-specific channel exists:
    /*
    if (!user?.id) return;
    const echo = getEcho();
    if (!echo) return;
    const userChannel = echo.private(`user.${user.id}`); // Example channel name

    userChannel.listen('.conversation.updated', (event) => {
        console.log("Conversation list update received:", event);
        // Refetch the conversation list or update specific conversation entry
        fetchConversations();
    });

    return () => {
        if (echo && user?.id) {
            echo.leave(`user.${user.id}`);
        }
    };
    */
    // Alternatively, refetch conversations periodically or on certain actions
  }, [user?.id]);

  // Memoized sorted list (using internal state)
  const sortedPitchHistories = useMemo(() => {
    let sorted = [...pitchHistories]; // Already filtered by fetchPitches based on currentView
    const statusOrder = { interested: 1, pending: 2, uninterested: 3 };
    sorted = sorted.map((p) => ({
      ...p,
      review_status: p.is_read ? "read" : "unread",
      interest_status: p.status || "pending", // is_archived is handled by view, not needed here for status value
    }));

    switch (sortBy) {
      case "score":
        sorted.sort(
          (a, b) =>
            (b.match_score ?? 0) - (a.match_score ?? 0) ||
            dayjs(b.created_at).diff(dayjs(a.created_at)),
        );
        break;
      case "date_desc":
        sorted.sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)));
        break;
      case "date_asc":
        sorted.sort((a, b) => dayjs(a.created_at).diff(dayjs(b.created_at)));
        break;
      case "status":
        sorted.sort(
          (a, b) =>
            (statusOrder[a.interest_status] || 99) -
              (statusOrder[b.interest_status] || 99) ||
            dayjs(b.created_at).diff(dayjs(a.created_at)),
        );
        break;
      case "unread":
        sorted.sort((a, b) => {
          if (a.review_status === "unread" && b.review_status !== "unread")
            return -1;
          if (a.review_status !== "unread" && b.review_status === "unread")
            return 1;
          return dayjs(b.created_at).diff(dayjs(a.created_at));
        });
        break;
      default:
        break;
    }
    return sorted;
  }, [pitchHistories, sortBy]);

  // --- Helper function for status filter ---
  const handleStatusChange = (status, checked) => {
    // Update temporary status state
    setTempFilterStatus((prev) => {
      let newStatus = [...prev];
      if (checked) {
        if (!newStatus.includes(status)) {
          newStatus.push(status);
        }
      } else {
        newStatus = newStatus.filter((s) => s !== status);
      }
      return newStatus;
    });
  };

  // Apply status filter
  const applyStatusFilter = () => {
    setFilterStatus(tempFilterStatus);
    setOpenStatusFilter(false);
  };

  // Reset status filter
  const resetStatusFilter = () => {
    setTempFilterStatus([]);
    setFilterStatus([]);
    setOpenStatusFilter(false);
  };

  // Helper function to get participant info
  const getOtherParticipant = (conversation) => {
    if (!conversation || !conversation.display_participants) return null;
    // Assumes display_participants contains the *other* participant object
    return conversation.display_participants[0] || null;
  };

  // Helper function to trigger chat from confirmation dialog
  const handleConfirmSendMessage = () => {
    if (pitchForSendMessageConfirm) {
      handleSendMessageClick(pitchForSendMessageConfirm);
    }
    setShowSendMessageConfirmDialog(false); // Close dialog regardless
    setPitchForSendMessageConfirm(null); // Clear the stored pitch
  };

  // Filter countries by search query
  const filteredCountries = useMemo(() => {
    return countries.filter((country) =>
      country.country_name
        .toLowerCase()
        .includes(searchCountryQuery.toLowerCase()),
    );
  }, [countries, searchCountryQuery]);

  // Filter sectors by search query
  const filteredSectors = useMemo(() => {
    return sectors.filter((sector) =>
      sector.name.toLowerCase().includes(searchSectorQuery.toLowerCase()),
    );
  }, [sectors, searchSectorQuery]);

  // Handle country checkbox selection
  const handleCountryChange = (event, country) => {
    const checked = event.target.checked;
    let newTempFilterCountries = [...tempFilterCountries];

    if (checked) {
      // Add country if not already included
      if (!newTempFilterCountries.some((c) => c.id === country.id)) {
        newTempFilterCountries.push(country);
      }
    } else {
      // Remove country
      newTempFilterCountries = newTempFilterCountries.filter(
        (c) => c.id !== country.id,
      );
    }

    setTempFilterCountries(newTempFilterCountries);
  };

  // Handle sector checkbox selection
  const handleSectorChange = (event, sector) => {
    const checked = event.target.checked;
    let newTempFilterSectors = [...tempFilterSectors];

    if (checked) {
      // Add sector if not already included
      if (!newTempFilterSectors.some((s) => s.id === sector.id)) {
        newTempFilterSectors.push(sector);
      }
    } else {
      // Remove sector
      newTempFilterSectors = newTempFilterSectors.filter(
        (s) => s.id !== sector.id,
      );
    }

    setTempFilterSectors(newTempFilterSectors);
  };

  // Apply country filter
  const applyCountryFilter = () => {
    setFilterCountries(tempFilterCountries);
    setOpenCountryFilter(false);
    setSearchCountryQuery("");
  };

  // Apply sector filter
  const applySectorFilter = () => {
    setFilterSectors(tempFilterSectors);
    setOpenSectorFilter(false);
    setSearchSectorQuery("");
  };

  // Reset country filter
  const resetCountryFilter = () => {
    setTempFilterCountries([]);
    setFilterCountries([]);
    setOpenCountryFilter(false);
    setSearchCountryQuery("");
  };

  // Reset sector filter
  const resetSectorFilter = () => {
    setTempFilterSectors([]);
    setFilterSectors([]);
    setOpenSectorFilter(false);
    setSearchSectorQuery("");
  };

  // --- JSX ---
  const renderPitchRow = (pitch, i) => {
    const startupInfo = getStartupInfo(pitch);
    const metadata = getPitchMetadata(pitch, startupInfo);
    const isInterested = pitch.status === "interested";
    const isUninterested = pitch.status === "uninterested";

    return (
      <div
        key={pitch.id || i}
        onClick={() => {
          if (currentView === "active") handleMarkRead(pitch.id);
          setDetailsPitch(pitch);
        }}
        className='py-4 cursor-pointer hover:bg-white/[0.03] rounded-lg px-2 -mx-2 transition-colors'
      >
        <div className='flex items-start gap-4'>
          {/* Logo */}
          <div className='relative flex-shrink-0 overflow-hidden rounded-lg size-[60px]'>
            {startupInfo?.logo_url ? (
              <Image
                objectFit='cover'
                layout='fill'
                src={startupInfo.logo_url}
                alt={`${startupInfo?.name || "Startup"} Logo`}
              />
            ) : (
              <div className='flex items-center justify-center w-full h-full text-lg font-bold rounded-lg bg-white/10 text-white/40'>
                {startupInfo?.name?.[0] || "?"}
              </div>
            )}
          </div>

          {/* Content */}
          <div className='flex flex-col flex-1 min-w-0 gap-2'>
            {/* Top row: name + score | metadata (desktop: side by side, mobile: stacked) */}
            <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-4'>
              <div className='flex flex-wrap items-center gap-2'>
                {!pitch.is_read && currentView === "active" && (
                  <span className='flex-shrink-0 inline-block w-2 h-2 bg-blue-500 rounded-full' />
                )}
                {currentView === "archived" && (
                  <ArchiveBox
                    size={14}
                    className='flex-shrink-0 text-white/50'
                  />
                )}
                <p className='text-xl font-bold'>
                  {startupInfo?.name || "Unknown Startup"}
                </p>
                {pitch.match_score !== null && pitch.match_score !== undefined && (
                  <ScoreBadge score={pitch.match_score} size='sm' />
                )}
              </div>
              {metadata.length > 0 && (
                <p className='text-xs text-white/40 lg:text-sm lg:flex-shrink-0 lg:whitespace-nowrap'>
                  {metadata.join(" · ")}
                </p>
              )}
            </div>

            {/* Score components — smart_pitch_bank only */}
            {smartFilteringMode === "smart_pitch_bank" &&
              pitch.score_components && (
                <div className='flex flex-wrap items-center gap-2'>
                  {Object.entries(pitch.score_components).map(([key, val]) => (
                    <ScoreComponentIndicator
                      key={key}
                      label={
                        key
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (char) => char.toUpperCase())
                      }
                      scoreRatio={getScoreComponentRatio(val)}
                    />
                  ))}
                </div>
              )}

            {/* View Profile link */}
            {!!startupInfo?.is_published && (
              <Link
                href={`/startups/${startupInfo.slug}-${startupInfo.id}`}
                className='text-[11px] w-fit text-orange-500 hover:opacity-80'
                target='_blank'
                rel='noopener noreferrer'
                onClick={(e) => e.stopPropagation()}
              >
                View Profile
              </Link>
            )}

            {/* Bottom row: action buttons */}
            <div
              className='flex flex-wrap items-center justify-between gap-2'
              onClick={(e) => e.stopPropagation()}
            >
              {/* Left group: Interested + Uninterested */}
              <div className='flex items-center gap-1.5'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => handleMarkInterested(pitch.id)}
                  className={`h-7 text-xs transition-colors ${
                    isInterested
                      ? "bg-green-500/15 border-green-500/40 text-green-400 hover:bg-green-500/25 hover:text-green-300"
                      : "text-white/60 hover:text-white"
                  }`}
                  disabled={currentView === "archived"}
                >
                  <ThumbsUp className='mr-1.5 h-3 w-3' />
                  Interested
                </Button>

                <Dialog
                  open={
                    feedbackDialogOpen &&
                    currentPitchForFeedback?.id === pitch.id
                  }
                  onOpenChange={setFeedbackDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => handleOpenFeedbackDialog(pitch)}
                      className={`h-7 text-xs transition-colors ${
                        isUninterested
                          ? "bg-red-500/15 border-red-500/40 text-red-400 hover:bg-red-500/25 hover:text-red-300"
                          : "text-white/60 hover:text-white"
                      }`}
                      disabled={currentView === "archived"}
                    >
                      <ThumbsDown className='mr-1.5 h-3 w-3' />
                      Uninterested
                    </Button>
                  </DialogTrigger>
                  <DialogContent className='sm:max-w-[425px] bg-background border-white/10'>
                    <DialogHeader>
                      <DialogTitle>Mark as Uninterested</DialogTitle>
                      <DialogDescription>
                        {`Optionally provide feedback to the startup founder of (${getStartupInfo(currentPitchForFeedback)?.name}). This will be included in an automatic email sent to the startup to let them know that ${vcName || "your organization"} is uninterested to pursue this any further at this time.`}
                      </DialogDescription>
                    </DialogHeader>
                    <div className='grid gap-4'>
                      <div>
                        <Label htmlFor='feedback' className='pb-1.5 block'>
                          Optional Feedback:
                        </Label>
                        <Textarea
                          id='feedback'
                          value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          placeholder="(Optional) Please help startup founders to understand why you're uninterested at this time."
                          className='bg-transparent border-white/10'
                          rows={4}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant='outline'
                        onClick={() => setFeedbackDialogOpen(false)}
                        disabled={isSubmittingUninterested}
                      >
                        Cancel
                      </Button>
                      <Button
                        type='submit'
                        variant='destructive'
                        onClick={() =>
                          handleMarkUninterested(
                            currentPitchForFeedback?.id,
                            feedbackText,
                          )
                        }
                        disabled={isSubmittingUninterested}
                      >
                        {isSubmittingUninterested ? (
                          <Loader2Icon className='w-4 h-4 mr-2 animate-spin' />
                        ) : null}
                        Submit Uninterested
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Desktop: original 3 separate buttons */}
              <div className='hidden lg:flex items-center gap-1.5'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => window.open(pitch.pitch_deck_file, "_blank")}
                  disabled={!pitch.pitch_deck_file || currentView === "archived"}
                  className='h-7 text-xs'
                >
                  <DownloadSimple className='mr-1.5 h-3 w-3' />
                  Download Pitch Deck
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => handleSendMessageClick(pitch)}
                  disabled={currentView === "archived"}
                  className='h-7 text-xs'
                >
                  <Chat className='mr-1.5 h-3 w-3' />
                  Send Message
                </Button>
                {currentView === "archived" ? (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => handleUnarchivePitch(pitch.id)}
                    className='h-7 text-xs text-yellow-500 border-yellow-500/50 hover:bg-yellow-500/10 hover:text-yellow-400'
                  >
                    <ArrowUUpLeft className='mr-1.5 h-3 w-3' />
                    Unarchive
                  </Button>
                ) : (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => handleArchivePitch(pitch.id)}
                    className='h-7 text-xs'
                  >
                    <ArchiveBox className='mr-1.5 h-3 w-3' />
                    Archive
                  </Button>
                )}
              </div>

              {/* Mobile: actions collapsed into a dropdown */}
              <div className='lg:hidden'>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-7 text-xs px-2'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DotsThreeVertical className='h-3.5 w-3.5' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end' className='bg-background border-white/10 min-w-[160px]'>
                    <DropdownMenuItem
                      onClick={() => window.open(pitch.pitch_deck_file, "_blank")}
                      disabled={!pitch.pitch_deck_file || currentView === "archived"}
                      className='text-xs gap-2 cursor-pointer'
                    >
                      <DownloadSimple className='h-3.5 w-3.5' />
                      Download Pitch Deck
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleSendMessageClick(pitch)}
                      disabled={currentView === "archived"}
                      className='text-xs gap-2 cursor-pointer'
                    >
                      <Chat className='h-3.5 w-3.5' />
                      Send Message
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className='bg-white/10' />
                    {currentView === "archived" ? (
                      <DropdownMenuItem
                        onClick={() => handleUnarchivePitch(pitch.id)}
                        className='text-xs gap-2 cursor-pointer text-yellow-400 focus:text-yellow-300'
                      >
                        <ArrowUUpLeft className='h-3.5 w-3.5' />
                        Unarchive
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => handleArchivePitch(pitch.id)}
                        className='text-xs gap-2 cursor-pointer'
                      >
                        <ArchiveBox className='h-3.5 w-3.5' />
                        Archive
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isSmartPitchBankEnabled = smartFilteringMode === "smart_pitch_bank";
  const bannerTitle = isSmartPitchBankEnabled
    ? "Smart Pitch Bank is on"
    : "Make your Pitch Bank smarter";
  const bannerBody = isSmartPitchBankEnabled
    ? "Recommended pitches are prioritized for immediate review, while other pitches remain available in your Pitch Bank."
    : "Set your pitch preferences so incoming opportunities are ranked by fit and surfaced with clearer match signals.";
  const bannerCta = isSmartPitchBankEnabled
    ? "Edit Smart Pitch Bank settings"
    : "Enable Smart Pitch Bank";

  return (
    <>
      {/* Notification Banner */}
      {vcId && vcSlug && (
        <div className='flex flex-col gap-3 p-5 mb-4 border sm:flex-row sm:items-center sm:justify-between rounded-xl border-blue-500/40 bg-gradient-to-r from-blue-950/50 to-blue-900/30'>
          <div className='flex-1 min-w-0'>
            <p className='font-semibold text-white'>
              {bannerTitle}
            </p>
            <p className='mt-2 text-xs text-white/60'>
              {bannerBody}
            </p>
          </div>
          <Button
            onClick={() => setPrefsModalOpen(true)}
            className='flex-shrink-0 gap-2 text-white bg-blue-500 border-0 hover:bg-blue-400'
          >
            <GearSix size={16} weight='fill' />
            {bannerCta}
          </Button>
        </div>
        //   <div className='flex flex-wrap items-center justify-between gap-2 px-4 py-3 mb-4 text-blue-200 border rounded-md bg-blue-900/20 border-blue-500/30'>
        //     <span>Adjust Pitching Criteria to get more Qualified Deal Flow</span>
        //     <Link
        //       href={
        //         isAngel
        //           ? `/angel-investors/${vcSlug}-${vcId}/edit`
        //           : `/investors/${vcSlug}-${vcId}/edit`
        //       }
        //       passHref
        //       legacyBehavior
        //     >
        //       <Button
        //         variant='outline'
        //         size='sm'
        //         className='ml-auto text-blue-200 bg-blue-500/10 border-blue-500/50 hover:bg-blue-500/20 hover:text-blue-100'
        //       >
        //         Adjust Criteria
        //       </Button>
        //     </Link>
        //   </div>
      )}

      {/* Mobile Filter Sheet */}
      <Sheet
        open={mobileFilterSheetOpen}
        onOpenChange={(open) => {
          if (open) {
            setTempFilterStatus([...filterStatus]);
            setTempFilterCountries([...filterCountries]);
            setTempFilterSectors([...filterSectors]);
          }
          setMobileFilterSheetOpen(open);
        }}
      >
        <SheetContent
          side='bottom'
          className='bg-background border-white/10 max-h-[85vh] flex flex-col rounded-t-2xl px-0 pb-0'
        >
          <SheetHeader className='px-4 pb-3 border-b border-white/10 text-left'>
            <SheetTitle>Sort &amp; Filter</SheetTitle>
          </SheetHeader>
          <ScrollArea className='flex-1'>
            <div className='px-4 py-4 space-y-6'>
              {/* Sort */}
              <div className='space-y-2'>
                <p className='text-sm font-medium text-white/40'>Sort By</p>
                <Select
                  value={sortBy}
                  onValueChange={(value) => {
                    setCurrentPage(1);
                    setSortBy(value);
                  }}
                >
                  <SelectTrigger className='w-full gap-3 px-3 text-xs h-9 bg-white/5 border-white/10'>
                    <CalendarBlank size={14} className='flex-shrink-0 opacity-70' />
                    <SelectValue placeholder='Sort by...' />
                  </SelectTrigger>
                  <SelectContent className='bg-background border-white/10'>
                    <SelectItem value='score'>Best Match</SelectItem>
                    <SelectItem value='date_desc'>Date Received (Newest)</SelectItem>
                    <SelectItem value='date_asc'>Date Received (Oldest)</SelectItem>
                    <SelectItem value='status'>Status (Interested First)</SelectItem>
                    <SelectItem value='unread'>Unread First</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div className='space-y-2'>
                <p className='text-sm font-medium text-white/40'>Status</p>
                {[
                  { value: "interested", label: "Startups of Interest" },
                  { value: "uninterested", label: "Uninterested Startups" },
                  { value: "unread", label: "Unread Pitches" },
                ].map(({ value, label }) => (
                  <div key={value} className='flex items-center gap-3 py-1'>
                    <input
                      type='checkbox'
                      id={`mobile-status-${value}`}
                      checked={tempFilterStatus.includes(value)}
                      onChange={(e) => handleStatusChange(value, e.target.checked)}
                      className='w-4 h-4'
                    />
                    <label htmlFor={`mobile-status-${value}`} className='text-sm cursor-pointer'>
                      {label}
                    </label>
                  </div>
                ))}
              </div>

              {/* Country */}
              <div className='space-y-2'>
                <p className='text-sm font-medium text-white/40'>Country</p>
                <Input
                  type='text'
                  placeholder='Search countries...'
                  value={searchCountryQuery}
                  onChange={(e) => setSearchCountryQuery(e.target.value)}
                  className='h-9 text-sm'
                />
                <div className='space-y-1 max-h-40 overflow-y-auto'>
                  {filteredCountries.map((country, idx) => (
                    <div key={idx} className='flex items-center gap-3 py-1'>
                      <input
                        type='checkbox'
                        id={`mobile-country-${country.id}`}
                        checked={!!tempFilterCountries.find((c) => c.id === country.id)}
                        onChange={(e) => handleCountryChange(e, country)}
                        className='w-4 h-4'
                      />
                      <label htmlFor={`mobile-country-${country.id}`} className='text-sm cursor-pointer'>
                        {country.country_name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sector */}
              <div className='space-y-2'>
                <p className='text-sm font-medium text-white/40'>Sector</p>
                <Input
                  type='text'
                  placeholder='Search sectors...'
                  value={searchSectorQuery}
                  onChange={(e) => setSearchSectorQuery(e.target.value)}
                  className='h-9 text-sm'
                />
                <div className='space-y-1 max-h-40 overflow-y-auto'>
                  {filteredSectors.filter((s) => s.name).map((sector, idx) => (
                    <div key={idx} className='flex items-center gap-3 py-1'>
                      <input
                        type='checkbox'
                        id={`mobile-sector-${sector.id}`}
                        checked={!!tempFilterSectors.find((s) => s.id === sector.id)}
                        onChange={(e) => handleSectorChange(e, sector)}
                        className='w-4 h-4'
                      />
                      <label htmlFor={`mobile-sector-${sector.id}`} className='text-sm cursor-pointer'>
                        {sector.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>

          <div className='px-4 py-4 border-t border-white/10 flex gap-2'>
            <Button
              variant='link'
              size='sm'
              onClick={() => {
                setFilterStatus([]);
                setTempFilterStatus([]);
                setFilterCountries([]);
                setTempFilterCountries([]);
                setFilterSectors([]);
                setTempFilterSectors([]);
                setCurrentPage(1);
                setMobileFilterSheetOpen(false);
              }}
              className='text-red-400 px-0'
            >
              Reset All
            </Button>
            <Button
              size='sm'
              onClick={() => {
                setFilterStatus(tempFilterStatus);
                setFilterCountries(tempFilterCountries);
                setFilterSectors(tempFilterSectors);
                setCurrentPage(1);
                setMobileFilterSheetOpen(false);
              }}
              className='flex-1 bg-white text-black hover:bg-white/90'
            >
              Apply Filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main Two-Column Layout */}
      <div className='flex items-start gap-5'>
        {/* LEFT: Pitch List */}
        <div className='flex-1 min-w-0'>
          {/* Mobile controls bar — hidden on desktop */}
          <div className='flex flex-wrap items-center gap-2 mb-4 lg:hidden'>
            {/* View toggle */}
            <div className='flex items-center gap-0.5 p-0.5 rounded-lg bg-white/5 border border-white/10'>
              <button
                onClick={() => { setCurrentPage(1); setCurrentView("active"); }}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  currentView === "active" ? "bg-orange-500 text-white" : "text-white/60"
                }`}
              >
                Active
              </button>
              <button
                onClick={() => { setCurrentPage(1); setCurrentView("archived"); }}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  currentView === "archived" ? "bg-orange-500 text-white" : "text-white/60"
                }`}
              >
                Archived
              </button>
            </div>

            {/* Sort select */}
            <Select
              value={sortBy}
              onValueChange={(value) => { setCurrentPage(1); setSortBy(value); }}
            >
              <SelectTrigger className='h-8 px-3 text-xs bg-white/5 border-white/10 w-auto gap-2 flex-shrink-0'>
                <CalendarBlank size={14} className='flex-shrink-0 opacity-70' />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className='bg-background border-white/10'>
                <SelectItem value='score'>Best Match</SelectItem>
                <SelectItem value='date_desc'>Date (Newest)</SelectItem>
                <SelectItem value='date_asc'>Date (Oldest)</SelectItem>
                <SelectItem value='status'>Status</SelectItem>
                <SelectItem value='unread'>Unread First</SelectItem>
              </SelectContent>
            </Select>

            {/* Filters button */}
            <button
              onClick={() => setMobileFilterSheetOpen(true)}
              className='flex items-center gap-2 px-3 h-8 text-xs font-medium border rounded-lg border-white/10 bg-white/5 hover:bg-white/10 transition-colors'
            >
              <Funnel size={14} className='opacity-70' />
              Filters
              {(filterStatus.length + filterCountries.length + filterSectors.length) > 0 && (
                <span className='bg-white text-black px-1.5 py-0.5 rounded text-[10px] font-bold leading-none'>
                  {filterStatus.length + filterCountries.length + filterSectors.length}
                </span>
              )}
            </button>
          </div>

          {/* Recommended / Other pill chips — only in smart_pitch_bank mode */}
          {smartFilteringMode === "smart_pitch_bank" && (
            <div className='flex items-center gap-2 mb-4'>
              <button
                onClick={() => {
                  setActiveTier("recommended");
                  setCurrentPage(1);
                }}
                className={`inline-flex items-center gap-1.5 px-3 pr-1 py-1 rounded-full text-xs font-medium border transition-colors ${
                  activeTier === "recommended"
                    ? "bg-orange-500/15 border-orange-500/40 text-orange-400"
                    : "bg-white/5 border-white/10 text-white/50 hover:text-white hover:border-white/20"
                }`}
              >
                ★ Recommended
                <span
                  className={`inline-flex items-center justify-center rounded-full px-1.5 min-w-[18px] h-[18px] text-[10px] font-semibold ${activeTier === "recommended" ? "bg-orange-500/25 text-orange-300" : "bg-white/10 text-white/40"}`}
                >
                  {recommendedCount}
                </span>
              </button>
              <button
                onClick={() => {
                  setActiveTier("other");
                  setCurrentPage(1);
                }}
                className={`inline-flex items-center gap-1.5 px-3 pr-1 py-1 rounded-full text-xs font-medium border transition-colors ${
                  activeTier === "other"
                    ? "bg-orange-500/15 border-orange-500/40 text-orange-400"
                    : "bg-white/5 border-white/10 text-white/50 hover:text-white hover:border-white/20"
                }`}
              >
                Other
                <span
                  className={`inline-flex items-center justify-center rounded-full px-1.5 min-w-[18px] h-[18px] text-[10px] font-semibold ${activeTier === "other" ? "bg-orange-500/25 text-orange-300" : "bg-white/10 text-white/40"}`}
                >
                  {otherCount}
                </span>
              </button>
            </div>
          )}

          {loadingPitches ? (
            <div className='flex items-center justify-center py-10'>
              <Loader2Icon className='w-6 h-6 mr-2 opacity-50 animate-spin' />
              <p className='opacity-50'>Loading pitches...</p>
            </div>
          ) : (
            <>
              {smartFilteringMode === "smart_pitch_bank" &&
                activeTier === "other" && (
                  <div className='flex items-start gap-2 px-3 py-2.5 mb-4 text-xs rounded-md bg-white/5 border border-white/10 text-white/50'>
                    <span className='flex-shrink-0 mt-0.5'>ⓘ</span>
                    <span>
                      These pitches scored below {recommendedThreshold} based on
                      your preferences. They may still be worth reviewing.
                    </span>
                  </div>
                )}
              <div className='divide-y divide-dashed divide-white/10'>
                {sortedPitchHistories.length > 0 ? (
                  sortedPitchHistories.map((pitch, i) =>
                    renderPitchRow(pitch, i),
                  )
                ) : (
                  <div className='flex flex-col items-center justify-center py-12 text-center'>
                    <Image
                      src={NotFound}
                      alt='No pitches found'
                      width={120}
                      height={120}
                      objectFit='contain'
                    />
                    <p className='mt-4 text-sm font-medium text-white/70'>
                      No pitches found.
                    </p>
                    <p className='mt-1 text-xs text-white/40'>
                      Try adjusting your filters or checking another view.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Pagination Controls */}
          {!loadingPitches && paginationData && paginationData.total > 0 && (
            <div className='flex items-center justify-between pt-4 mt-4 border-t border-white/10'>
              <p className='text-sm text-white/70'>
                Showing{" "}
                <span className='font-medium text-white'>
                  {paginationData.from || 0}
                </span>
                -
                <span className='font-medium text-white'>
                  {paginationData.to || 0}
                </span>{" "}
                of{" "}
                <span className='font-medium text-white'>
                  {paginationData.total || 0}
                </span>{" "}
                pitches
              </p>
              <div className='flex items-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={
                    currentPage === 1 || paginationData.current_page === 1
                  }
                  className='h-auto px-2 py-1 text-xs bg-transparent border-white/10 hover:bg-white/10 disabled:opacity-50'
                >
                  <CaretLeft size={14} className='mr-1' />
                  Previous
                </Button>
                <span className='text-sm text-white/70'>
                  Page{" "}
                  <span className='font-medium text-white'>
                    {paginationData.current_page || currentPage}
                  </span>{" "}
                  of{" "}
                  <span className='font-medium text-white'>
                    {paginationData.last_page || 0}
                  </span>
                </span>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(paginationData.last_page, prev + 1),
                    )
                  }
                  disabled={
                    currentPage === paginationData.last_page ||
                    paginationData.current_page === paginationData.last_page
                  }
                  className='h-auto px-2 py-1 text-xs bg-transparent border-white/10 hover:bg-white/10 disabled:opacity-50'
                >
                  Next
                  <CaretRight size={14} className='ml-1' />
                </Button>
              </div>
            </div>
          )}

          {/* Pitch Details Sheet — controlled by detailsPitch state */}
          <Sheet
            open={!!detailsPitch}
            onOpenChange={(open) => {
              if (!open) setDetailsPitch(null);
            }}
          >
            <SheetContent className='bg-white text-background w-full !max-w-none lg:!max-w-lg px-0 flex flex-col gap-0 py-4'>
              {detailsPitch &&
                (() => {
                  const p = detailsPitch;
                  const si = getStartupInfo(p);
                  return (
                    <>
                      <SheetHeader className='px-4 pb-4 space-y-1 text-left border-b lg:px-5 border-black/10'>
                        <SheetTitle className='text-background'>
                          Pitch Details
                        </SheetTitle>
                        <SheetDescription className='text-background/60'>
                          Pitch pitched to {vcName || "your organization"} on{" "}
                          {dayjs(p.created_at).format("MMM DD, YYYY")}. This is
                          their submission form.
                        </SheetDescription>
                      </SheetHeader>
                      <div className='flex-1 px-4 py-4 space-y-5 overflow-y-auto text-sm lg:px-5'>
                        {p.match_score !== null && p.match_score !== undefined && (
                          <div className='p-4 border border-gray-200 rounded-lg bg-gray-50'>
                            <div className='flex items-center gap-3 mb-3'>
                              <div
                                className={`flex items-center justify-center w-14 h-14 rounded-xl text-2xl font-bold ${p.match_score === null ? "bg-gray-200 text-gray-400" : p.match_score >= 80 ? "bg-green-100 text-green-700" : p.match_score >= 60 ? "bg-blue-100 text-blue-700" : p.match_score >= 40 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}
                              >
                                {p.match_score ?? "--"}
                              </div>
                              <div>
                                <p className='font-semibold text-background'>
                                  Match Score
                                </p>
                                {p.match_tier === "recommended" && (
                                  <p className='text-xs font-medium text-green-600'>
                                    ★ Recommended
                                  </p>
                                )}
                                {p.match_tier === "other" && (
                                  <p className='text-xs text-gray-500'>
                                    ○ Other
                                  </p>
                                )}
                                {p.similarity_band && (
                                  <p className='text-xs text-gray-500 mt-0.5'>
                                    Portfolio Similarity:{" "}
                                    {p.similarity_band.charAt(0).toUpperCase() +
                                      p.similarity_band.slice(1)}
                                  </p>
                                )}
                              </div>
                            </div>
                            {p.score_reasons && p.score_reasons.length > 0 && (
                              <div>
                                <p className='mb-1 text-xs font-medium text-background/70'>
                                  Why this score:
                                </p>
                                <ul className='space-y-0.5'>
                                  {p.score_reasons.map((reason, idx) => (
                                    <li
                                      key={idx}
                                      className='flex items-start gap-1 text-xs text-background/60'
                                    >
                                      <span className='mt-0.5'>•</span>
                                      <span>{reason}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        <ul className='space-y-4'>
                          <li>
                            <p className='font-medium'>Startup</p>
                            <div className='mt-1 space-y-0.5 opacity-80 text-background'>
                              <p>Name: {si?.name || "Not provided"}</p>
                              <p>
                                Website:{" "}
                                {si?.website ? (
                                  <a
                                    href={
                                      si.website.startsWith("http")
                                        ? si.website
                                        : `http://${si.website}`
                                    }
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='text-orange-600 break-all hover:underline'
                                  >
                                    {si.website}
                                  </a>
                                ) : (
                                  "Not provided"
                                )}
                              </p>
                              <p>
                                Owner Email: {p.owner_email || "Not provided"}
                              </p>
                            </div>
                          </li>
                          <li>
                            <p className='font-medium'>Incorporation</p>
                            <p className='mt-1 leading-normal opacity-80 text-background'>
                              {p.incorporation || "Not provided"}
                            </p>
                          </li>
                          <li>
                            <p className='font-medium'>Traction</p>
                            <p className='mt-1 leading-normal opacity-80 text-background'>
                              {p.traction || "Not provided"}
                            </p>
                          </li>
                          <li>
                            <p className='font-medium'>
                              Relevant Domain Experience
                            </p>
                            <p className='mt-1 leading-normal opacity-80 text-background'>
                              {p.relevant_domain_experience || "Not provided"}
                            </p>
                          </li>
                          <li>
                            <p className='font-medium'>Linkedin Page</p>
                            <p className='mt-1 leading-normal break-all opacity-80 text-background'>
                              {p.linkedin_page ? (
                                <a
                                  href={
                                    p.linkedin_page.startsWith("http")
                                      ? p.linkedin_page
                                      : `http://${p.linkedin_page}`
                                  }
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='text-orange-600 hover:underline'
                                >
                                  {p.linkedin_page}
                                </a>
                              ) : (
                                "Not provided"
                              )}
                            </p>
                          </li>
                          <li>
                            <p className='font-medium'>
                              Current Investment Round
                            </p>
                            <p className='mt-1 leading-normal prose-sm prose opacity-80 text-background prose-orange max-w-none'>
                              {p.current_investment_round || "Not provided"}
                            </p>
                          </li>
                          <li>
                            <p className='font-medium'>Equity Offered</p>
                            <p className='mt-1 leading-normal opacity-80 text-background'>
                              {p.equity_offered
                                ? Math.round(p.equity_offered) + "%"
                                : "-"}
                            </p>
                          </li>
                          <li>
                            <p className='font-medium'>Use of Invested Funds</p>
                            <div className='mt-1 leading-normal prose-sm prose prose-orange max-w-none text-background opacity-80'>
                              {p.use_of_invested_funds || "Not provided"}
                            </div>
                          </li>
                          <li>
                            <p className='font-medium'>
                              Why do you want {vcName || "this organization"} as
                              an investor and why now?
                            </p>
                            <div className='mt-1 leading-normal prose-sm prose prose-orange max-w-none text-background opacity-80'>
                              {p.reason || "Not provided"}
                            </div>
                          </li>
                          <li>
                            <p className='font-medium'>Pitch Deck File</p>
                            <div className='mt-1.5 opacity-80 text-background'>
                              {p.pitch_deck_file ? (
                                <a
                                  href={p.pitch_deck_file}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='text-orange-600 break-all hover:underline'
                                >
                                  {p.pitch_deck_file.split("/").pop()}
                                </a>
                              ) : (
                                <p>No pitch deck attached.</p>
                              )}
                            </div>
                          </li>
                          <li>
                            <p className='font-medium'>Other Files Attached</p>
                            <div className='mt-1.5 opacity-80 text-background space-y-1'>
                              {p.attached_files &&
                              p.attached_files.length > 0 ? (
                                p.attached_files.map((fileUrl, index) => (
                                  <p key={index}>
                                    <a
                                      href={fileUrl}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='text-orange-600 break-all hover:underline'
                                    >
                                      {fileUrl.split("/").pop() ||
                                        `Attachment ${index + 1}`}
                                    </a>
                                  </p>
                                ))
                              ) : (
                                <p>No other files attached.</p>
                              )}
                            </div>
                          </li>
                        </ul>
                      </div>
                      <SheetFooter className='px-4 pt-4 border-t lg:px-5 border-black/10'>
                        <SheetClose asChild>
                          <Button variant='secondary' className='w-full'>
                            Close Details
                          </Button>
                        </SheetClose>
                      </SheetFooter>
                    </>
                  );
                })()}
            </SheetContent>
          </Sheet>
        </div>

        {/* RIGHT: Controls Panel — desktop only */}
        <div className='hidden lg:block flex-shrink-0 w-56 pt-1 space-y-5'>
          {/* View Section */}
          <div className='space-y-2'>
            <p className='text-sm font-medium text-white/40'>View</p>
            <button
              onClick={() => {
                setCurrentPage(1);
                setCurrentView("active");
              }}
              className={`flex items-center justify-center w-full h-8 px-3 rounded-lg text-xs font-medium border transition-colors ${
                currentView === "active"
                  ? "border-orange-500 text-orange-500 bg-transparent"
                  : "border-white/10 text-white bg-white/5 hover:bg-white/10"
              }`}
            >
              Active Pitches
            </button>
            <button
              onClick={() => {
                setCurrentPage(1);
                setCurrentView("archived");
              }}
              className={`flex items-center justify-center w-full h-8 px-3 rounded-lg text-xs font-medium border transition-colors ${
                currentView === "archived"
                  ? "border-orange-500 text-orange-500 bg-transparent"
                  : "border-white/10 text-white bg-white/5 hover:bg-white/10"
              }`}
            >
              Archived Pitches
            </button>
          </div>

          {/* Sort & Filter Section */}
          <div className='space-y-2'>
            <p className='text-sm font-medium text-white/40'>Sort & Filter</p>

            {/* Sort select */}
            <Select
              value={sortBy}
              onValueChange={(value) => {
                setCurrentPage(1);
                setSortBy(value);
              }}
            >
              <SelectTrigger className='w-full gap-3 px-3 text-xs h-8 bg-white/5 border-white/10'>
                <CalendarBlank size={16} className='flex-shrink-0 opacity-70' />
                <SelectValue placeholder='Sort by...' />
              </SelectTrigger>
              <SelectContent className='bg-background border-white/10'>
                <SelectItem value='score'>Best Match</SelectItem>
                <SelectItem value='date_desc'>
                  Date Received (Newest)
                </SelectItem>
                <SelectItem value='date_asc'>Date Received (Oldest)</SelectItem>
                <SelectItem value='status'>
                  Status (Interested First)
                </SelectItem>
                <SelectItem value='unread'>Unread First</SelectItem>
              </SelectContent>
            </Select>

            {/* Status filter */}
            <Popover
              open={openStatusFilter}
              onOpenChange={(open) => {
                if (open) setTempFilterStatus([...filterStatus]);
                setOpenStatusFilter(open);
              }}
            >
              <PopoverTrigger asChild>
                <button className='flex items-center w-full gap-3 px-3 text-xs font-medium transition-colors border rounded-lg h-8 border-white/10 bg-white/5 hover:bg-white/10'>
                  <CircleDashed
                    size={14}
                    className='flex-shrink-0 opacity-70'
                  />
                  <span>Status</span>
                  {filterStatus.length > 0 && (
                    <span className='ml-auto rounded bg-white text-black px-1.5 py-0.5 text-xs font-semibold'>
                      {filterStatus.length}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                className='w-56 bg-background border-white/10'
                align='start'
              >
                <h4 className='px-2 pt-2 text-sm font-medium'>Status</h4>
                <div className='p-2'>
                  <div className='flex items-center p-2 text-xs'>
                    <input
                      type='checkbox'
                      checked={tempFilterStatus.includes("interested")}
                      onChange={(e) =>
                        handleStatusChange("interested", e.target.checked)
                      }
                      id='status-interested'
                      className='mr-2'
                    />
                    <label htmlFor='status-interested'>
                      Startups of Interest
                    </label>
                  </div>
                  <div className='flex items-center p-2 text-xs'>
                    <input
                      type='checkbox'
                      checked={tempFilterStatus.includes("uninterested")}
                      onChange={(e) =>
                        handleStatusChange("uninterested", e.target.checked)
                      }
                      id='status-uninterested'
                      className='mr-2'
                    />
                    <label htmlFor='status-uninterested'>
                      Uninterested Startups
                    </label>
                  </div>
                  <div className='flex items-center p-2 text-xs'>
                    <input
                      type='checkbox'
                      checked={tempFilterStatus.includes("unread")}
                      onChange={(e) =>
                        handleStatusChange("unread", e.target.checked)
                      }
                      id='status-unread'
                      className='mr-2'
                    />
                    <label htmlFor='status-unread'>Unread Pitches</label>
                  </div>
                </div>
                <div className='flex items-center justify-between p-2 pt-2 mt-2 border-t bg-background'>
                  <Button
                    onClick={resetStatusFilter}
                    size='sm'
                    variant='link'
                    className='text-xs text-red-500 h-7'
                    disabled={filterStatus.length <= 0}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={applyStatusFilter}
                    size='sm'
                    variant='outline'
                    className='text-xs h-7'
                  >
                    Apply Filter
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {/* Country filter */}
            <Popover
              open={openCountryFilter}
              onOpenChange={(open) => {
                if (open) setTempFilterCountries([...filterCountries]);
                setOpenCountryFilter(open);
              }}
            >
              <PopoverTrigger asChild>
                <button className='flex items-center w-full gap-3 px-3 text-xs font-medium transition-colors border rounded-lg h-8 border-white/10 bg-white/5 hover:bg-white/10'>
                  <Flag size={14} className='flex-shrink-0 opacity-70' />
                  <span>Country</span>
                  {filterCountries.length > 0 && (
                    <span className='ml-auto rounded bg-white text-black px-1.5 py-0.5 text-xs font-semibold'>
                      {filterCountries.length}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                className='w-64 bg-background border-white/10 h-80'
                align='start'
              >
                <div className='p-2'>
                  <Input
                    type='text'
                    placeholder='Search countries...'
                    value={searchCountryQuery}
                    onChange={(e) => setSearchCountryQuery(e.target.value)}
                    className='mb-2'
                  />
                </div>
                <ScrollArea className='w-full h-56'>
                  {filteredCountries.map((country, index) => (
                    <div key={index} className='flex items-center p-2 text-xs'>
                      <input
                        type='checkbox'
                        checked={
                          !!tempFilterCountries.find((c) => c.id === country.id)
                        }
                        onChange={(e) => handleCountryChange(e, country)}
                        id={`country-${country.id}`}
                        className='mr-2'
                      />
                      <label htmlFor={`country-${country.id}`}>
                        {country.country_name}
                        {country.prefix ? ` (${country.prefix})` : ""}
                      </label>
                    </div>
                  ))}
                </ScrollArea>
                <div className='flex items-center justify-between p-2 pt-2 mt-2 border-t bg-background'>
                  <Button
                    onClick={resetCountryFilter}
                    size='sm'
                    variant='link'
                    className='text-xs text-red-500 h-7'
                    disabled={filterCountries.length <= 0}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={applyCountryFilter}
                    size='sm'
                    variant='outline'
                    className='text-xs h-7'
                  >
                    Apply Filter
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {/* Sector filter */}
            <Popover
              open={openSectorFilter}
              onOpenChange={(open) => {
                if (open) setTempFilterSectors([...filterSectors]);
                setOpenSectorFilter(open);
              }}
            >
              <PopoverTrigger asChild>
                <button className='flex items-center w-full gap-3 px-3 text-xs font-medium transition-colors border rounded-lg h-8 border-white/10 bg-white/5 hover:bg-white/10'>
                  <Tag size={14} className='flex-shrink-0 opacity-70' />
                  <span>Sector</span>
                  {filterSectors.length > 0 && (
                    <span className='ml-auto rounded bg-white text-black px-1.5 py-0.5 text-xs font-semibold'>
                      {filterSectors.length}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                className='w-64 bg-background border-white/10 h-80'
                align='start'
              >
                <div className='p-2'>
                  <Input
                    type='text'
                    placeholder='Search sectors...'
                    value={searchSectorQuery}
                    onChange={(e) => setSearchSectorQuery(e.target.value)}
                    className='mb-2'
                  />
                </div>
                <ScrollArea className='w-full h-56'>
                  {filteredSectors
                    .filter((s) => s.name)
                    .map((sector, index) => (
                      <div
                        key={index}
                        className='flex items-center p-2 text-xs'
                      >
                        <input
                          type='checkbox'
                          checked={
                            !!tempFilterSectors.find((s) => s.id === sector.id)
                          }
                          onChange={(e) => handleSectorChange(e, sector)}
                          id={`sector-${sector.id}`}
                          className='mr-2'
                        />
                        <label htmlFor={`sector-${sector.id}`}>
                          {sector.name}
                        </label>
                      </div>
                    ))}
                </ScrollArea>
                <div className='flex items-center justify-between p-2 pt-2 mt-2 border-t bg-background'>
                  <Button
                    onClick={resetSectorFilter}
                    size='sm'
                    variant='link'
                    className='text-xs text-red-500 h-7'
                    disabled={filterSectors.length <= 0}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={applySectorFilter}
                    size='sm'
                    variant='outline'
                    className='text-xs h-7'
                  >
                    Apply Filter
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {(filterStatus.length > 0 ||
              filterCountries.length > 0 ||
              filterSectors.length > 0) && (
              <Button
                variant='link'
                size='sm'
                onClick={() => {
                  setFilterStatus([]);
                  setTempFilterStatus([]);
                  setFilterCountries([]);
                  setTempFilterCountries([]);
                  setFilterSectors([]);
                  setTempFilterSectors([]);
                  setCurrentPage(1);
                }}
                className='justify-start w-full h-8 px-0 text-xs text-orange-500'
              >
                Clear All Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Floating Chat Panel */}
      {isChatPanelOpen && (
        <div className='fixed bottom-0 right-4 w-80 bg-background border border-white/10 rounded-t-lg shadow-xl flex flex-col z-50 h-[500px] max-h-[70vh]'>
          {chatViewMode === "list" && (
            <>
              <div className='flex items-center justify-between p-3 border-b border-white/10'>
                <h2 className='text-base font-semibold'>Messages</h2>
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() => setIsChatPanelOpen(false)}
                  className='w-8 h-8 rounded-full hover:bg-white/10'
                >
                  <X size={16} />
                </Button>
              </div>
              <ScrollArea className='flex-1'>
                {loadingConversations && vcConversations.length === 0 ? (
                  <div className='p-4 text-sm text-center opacity-50'>
                    Loading conversations...
                  </div>
                ) : !loadingConversations && vcConversations.length === 0 ? (
                  <div className='p-4 text-sm text-center opacity-50'>
                    No conversations yet.
                  </div>
                ) : (
                  <nav className='p-2 space-y-1'>
                    {vcConversations.map((conv) => {
                      const participant = getOtherParticipant(conv);
                      return (
                        <button
                          key={conv.uuid}
                          onClick={() => handleSelectConversation(conv)}
                          className={`w-full text-left p-2 rounded-md flex items-center gap-3 hover:bg-white/5 transition-colors`}
                          disabled={loadingMessages}
                        >
                          <Image
                            src={participant?.logo_url || DummyLogo}
                            alt={participant?.name || "Avatar"}
                            width={32}
                            height={32}
                            className='flex-shrink-0 w-8 h-8 bg-gray-700 rounded-full'
                            objectFit='cover'
                          />
                          <div className='flex-1 overflow-hidden'>
                            <p className='text-sm font-medium truncate'>
                              {participant?.name || `Conversation ${conv.id}`}
                            </p>
                            <p className='text-[11px] truncate opacity-40 mt-0.5 font-normal'>
                              {conv.latestMessage?.content || "No messages yet"}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </nav>
                )}
              </ScrollArea>
            </>
          )}
          {chatViewMode === "detail" && selectedChatConversation && (
            <>
              <div className='flex items-center gap-2 p-3 border-b border-white/10'>
                {/* <Button
                  variant='ghost'
                  size='icon'
                  className='flex-shrink-0 w-8 h-8 rounded-full hover:bg-white/10'
                  onClick={() => {
                    setChatViewMode("list");
                  }}
                >
                  <ArrowLeft className='w-4 h-4' />
                </Button> */}
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() => setIsChatPanelOpen(false)}
                  className='w-8 h-8 ml-auto rounded-full hover:bg-white/10'
                >
                  <X size={16} />
                </Button>
              </div>
              <div className='flex-1 min-h-0 overflow-hidden'>
                <ChatWindow
                  messages={currentMessages}
                  currentUser={user}
                  otherParticipant={getOtherParticipant(
                    selectedChatConversation,
                  )}
                  onSendMessage={handleSendMessageSubmit}
                  isLoadingMessages={loadingMessages}
                  isSendingMessage={sendingMessage}
                  showHeader={true}
                  headerTitle={
                    getOtherParticipant(selectedChatConversation)?.name
                  }
                  startupInfo={getStartupInfo(currentChatPitch)}
                />
              </div>
            </>
          )}
          {chatViewMode === "detail" &&
            !selectedChatConversation &&
            !loadingMessages && (
              <div className='flex flex-col items-center justify-center flex-1 p-4'>
                <p className='text-sm text-center opacity-50'>
                  Could not load chat details.
                </p>
                <Button
                  variant='link'
                  onClick={() => {
                    setChatViewMode("list");
                    setIsChatPanelOpen(true);
                  }}
                  className='mt-2'
                >
                  Back to conversations
                </Button>
              </div>
            )}
          {chatViewMode === "detail" && loadingMessages && (
            <div className='flex items-center justify-center flex-1'>
              <Loader2Icon className='w-5 h-5 mr-2 opacity-50 animate-spin' />
              <span className='text-sm opacity-50'>Loading messages...</span>
            </div>
          )}
        </div>
      )}

      {/* Investor Preferences Modal */}
      <InvestorPreferencesModal
        vcId={vcId}
        open={prefsModalOpen}
        onOpenChange={handlePrefsModalOpenChange}
        onSaved={(newMode) => {
          const nextTier = "recommended";
          const refreshPitches = (forceRecommended = false) => {
            const latest = latestFetchStateRef.current;
            const tier = forceRecommended
              ? nextTier
              : latest.activeTier || nextTier;
            if (!forceRecommended && latest.smartFilteringMode !== newMode) {
              return;
            }
            fetchPitches(
              1,
              latest.sortBy || sortBy,
              latest.filterStatus || filterStatus,
              latest.currentView || currentView,
              latest.filterCountries || filterCountries,
              latest.filterSectors || filterSectors,
              tier,
              newMode,
            );
          };

          setSmartFilteringMode(newMode);
          setActiveTier(nextTier);
          setCurrentPage(1);
          refreshPitches(true);
          if (newMode === "smart_pitch_bank") {
            setTimeout(refreshPitches, 1500);
            setTimeout(refreshPitches, 4000);
          }
        }}
      />

      {/* Send Message Confirmation Dialog */}
      <Dialog
        open={showSendMessageConfirmDialog}
        onOpenChange={(open) => {
          setShowSendMessageConfirmDialog(open);
          if (!open) setPitchForSendMessageConfirm(null); // Clear pitch on close
        }}
      >
        <DialogContent className='sm:max-w-[425px] bg-background border-white/10'>
          <DialogHeader>
            <DialogTitle>Send Message?</DialogTitle>
            <DialogDescription>
              {`Would you like to send a message to the startup (${
                pitchForSendMessageConfirm?.startup?.name || "this startup"
              }) now?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant='outline'>No</Button>
            </DialogClose>
            <Button type='button' onClick={handleConfirmSendMessage}>
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
